'use client';

import { useEffect, useState } from 'react';
import { Box, Menu, MenuItem, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { signIn, useSession } from 'next-auth/react';
import { AUTH_PROVIDER, SESSION_STATUS } from '@/constants/auth';
import { SNACKBAR_VARIANT } from '@/constants/notifications';
import { SEARCH_TYPE } from '@/constants/searchType';
import { RESULT_DISPLAY_TYPE } from '@/constants/resultDisplay';
import { useAnimalDisplayType } from '@/hooks/publicSearch/animal/useAnimalDisplayType';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import type { SearchType } from '@/hooks/publicSearch/useSearchType';
import { useSearch } from '@/hooks/publicSearch/useSearch';
import {
  downloadPublicSearchGridCsv,
  downloadPublicSearchGridExcel,
  printPublicSearchGridIframe,
  publicSearchExportBaseFileName,
  type PublicSearchGridExportOptions,
} from '@/lib/publicSearch/publicSearchGridExport';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { enqueueSnackbar } from '@/providers';
import useGridStore from '@/publicSearch/store/useGridStore';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import { searchApiRef } from '../../searchSection/PublicSearchDataGrid';
import { useCallbackUrl } from '../hooks/useCallbackUrl';
import { StyledSearchIconButton } from '../../searchPage/SearchActions';
import {
  searchControlIconButtonSx,
  searchControlMenuDivider,
  searchControlMenuItemSx,
} from './searchControlStyles';

const exportListSearchTypes: SearchType[] = [
  SEARCH_TYPE.ANIMAL,
  SEARCH_TYPE.MEMBERS,
  SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS,
  SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS,
];

export default function SearchExportControl() {
  const searchType = useSearchType();
  const { showSearchWelcome } = useSearch();
  const openResult = useSearchStore((state) => state.openResult);
  const { resultDisplay } = useAnimalDisplayType();
  const { status } = useSession();
  const callbackUrl = useCallbackUrl();
  const society = usePublicSearchContext((context) => context.society);
  const { showEBVs, showEBVAccuracy, showPercentiles } = useGridStore();
  const { isBlackRedAngus } = useEbvDisplayOptions();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [exportingExcel, setExportingExcel] = useState<boolean>(false);

  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;
  const open = Boolean(anchorEl);
  const showExport =
    !showSearchWelcome &&
    openResult &&
    resultDisplay !== RESULT_DISPLAY_TYPE.GALLERY &&
    exportListSearchTypes.includes(searchType);
  const exportOptions: PublicSearchGridExportOptions = {
    showEBVs,
    showEBVAccuracy,
    showPercentiles,
    isBlackRedAngusStrain: isBlackRedAngus,
  };

  useEffect(() => {
    if (!isAuthenticated && anchorEl) setAnchorEl(null);
  }, [anchorEl, isAuthenticated]);

  if (!showExport) return null;

  const closeMenu = () => setAnchorEl(null);
  const notifyGridNotReady = () => {
    enqueueSnackbar('Results are not ready to export yet.', {
      variant: SNACKBAR_VARIANT.WARNING,
    });
  };
  const getGridAndBaseName = () => {
    const api = searchApiRef.current;
    if (!api) {
      notifyGridNotReady();
      return null;
    }
    return {
      api,
      baseName: publicSearchExportBaseFileName(
        society.societyCode ?? undefined,
      ),
    };
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isAuthenticated) {
      void signIn(AUTH_PROVIDER, { callbackUrl });
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handlePrint = () => {
    if (!isAuthenticated) return;
    closeMenu();
    const grid = getGridAndBaseName();
    if (!grid) return;
    try {
      printPublicSearchGridIframe(
        grid.api,
        grid.baseName.replace(/_/g, ' '),
        exportOptions,
      );
    } catch {
      enqueueSnackbar('Print failed.', { variant: SNACKBAR_VARIANT.ERROR });
    }
  };

  const handleCsv = () => {
    if (!isAuthenticated) return;
    closeMenu();
    const grid = getGridAndBaseName();
    if (!grid) return;
    downloadPublicSearchGridCsv(grid.api, grid.baseName, exportOptions);
  };

  const handleExcel = async () => {
    if (!isAuthenticated) return;
    closeMenu();
    const grid = getGridAndBaseName();
    if (!grid) return;
    setExportingExcel(true);
    try {
      await downloadPublicSearchGridExcel(
        grid.api,
        grid.baseName,
        exportOptions,
      );
    } catch {
      enqueueSnackbar('Excel export failed.', {
        variant: SNACKBAR_VARIANT.ERROR,
      });
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Tooltip
          title={
            isAuthenticated
              ? 'Export the results on screen: current page and visible columns.'
              : 'Sign in to export search results (print, CSV, or Excel).'
          }
          placement="bottom"
        >
          <StyledSearchIconButton
            sx={searchControlIconButtonSx(isAuthenticated && open)}
            onClick={handleMenuClick}
            data-dd-action-name="Export: Open Menu"
            aria-label={
              isAuthenticated
                ? 'Export visible results'
                : 'Sign in to export search results'
            }
            aria-haspopup={isAuthenticated ? 'true' : undefined}
            aria-expanded={isAuthenticated && open ? 'true' : undefined}
          >
            <FileDownloadOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
          </StyledSearchIconButton>
        </Tooltip>
        {!isAuthenticated && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -3,
              left: -4,
              width: 14,
              height: 18,
              borderRadius: '4px',
              backgroundColor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={isAuthenticated && open}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={closeMenu}
      >
        <MenuItem
          data-dd-action-name="Export: Print"
          onClick={handlePrint}
          sx={{
            ...searchControlMenuItemSx,
            borderBottom: searchControlMenuDivider,
          }}
        >
          Print current page
        </MenuItem>
        <MenuItem
          data-dd-action-name="Export: CSV"
          onClick={handleCsv}
          sx={{
            ...searchControlMenuItemSx,
            borderBottom: searchControlMenuDivider,
          }}
        >
          Download current page (CSV)
        </MenuItem>
        <MenuItem
          data-dd-action-name="Export: Excel"
          disabled={exportingExcel}
          onClick={() => void handleExcel()}
          sx={searchControlMenuItemSx}
        >
          {exportingExcel
            ? 'Preparing Excel…'
            : 'Download current page (Excel)'}
        </MenuItem>
      </Menu>
    </>
  );
}
